import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';

const API_URL = process.env.REACT_APP_API_URL;

function Dashboard({ token, onLogout }) {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    startDate: '',
    endDate: '',
    os: 'all'
  });

  useEffect(() => {
    fetchStats();
  }, []);

  const fetchStats = async () => {
    setLoading(true);
    try {
      const params = {};
      if (filters.startDate) params.startDate = filters.startDate;
      if (filters.endDate) params.endDate = filters.endDate;
      if (filters.os !== 'all') params.os = filters.os;

      const response = await axios.get(`${API_URL}/clicks/stats`, {
        headers: { Authorization: `Bearer ${token}` },
        params
      });

      setStats(response.data);
    } catch (err) {
      console.error('Error fetching stats:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (e) => {
    setFilters({
      ...filters,
      [e.target.name]: e.target.value
    });
  };

  const handleApplyFilter = () => {
    fetchStats();
  };

  const handleExport = async () => {
    try {
      const params = {};
      if (filters.startDate) params.startDate = filters.startDate;
      if (filters.endDate) params.endDate = filters.endDate;
      if (filters.os !== 'all') params.os = filters.os;

      const response = await axios.get(`${API_URL}/clicks/export`, {
        headers: { Authorization: `Bearer ${token}` },
        params,
        responseType: 'blob'
      });

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `baocao-truy-cap-${new Date().toISOString().split('T')[0]}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (err) {
      console.error('Error exporting:', err);
    }
  };

  const getChartData = () => {
    if (!stats || !stats.clicks) return [];

    const dateMap = {};
    stats.clicks.forEach(click => {
      const date = new Date(click.timestamp).toLocaleDateString('vi-VN');
      if (!dateMap[date]) {
        dateMap[date] = { date, iOS: 0, Android: 0, Other: 0 };
      }
      dateMap[date][click.os]++;
    });

    return Object.values(dateMap).reverse().slice(-30);
  };

  return (
    <div>
      <div className="header">
        <div className="header-content">
          <h1>PharmaCom - Quản lý Link Tracking</h1>
          <button className="btn btn-primary" onClick={onLogout}>
            Đăng xuất
          </button>
        </div>
      </div>

      <div className="container">
        {loading ? (
          <div className="loading">Đang tải dữ liệu...</div>
        ) : (
          <>
            <div className="stats-grid">
              <div className="stat-card total">
                <h3>Tổng lượt truy cập</h3>
                <div className="number">{stats?.total || 0}</div>
              </div>
              <div className="stat-card ios">
                <h3>iOS</h3>
                <div className="number">{stats?.ios || 0}</div>
              </div>
              <div className="stat-card android">
                <h3>Android</h3>
                <div className="number">{stats?.android || 0}</div>
              </div>
              <div className="stat-card other">
                <h3>Khác</h3>
                <div className="number">{stats?.other || 0}</div>
              </div>
            </div>

            <div className="card">
              <h3 style={{ marginBottom: '20px', fontSize: '20px', fontWeight: '700' }}>
                🔍 Bộ lọc dữ liệu
              </h3>
              <div className="filters">
                <div className="filter-item">
                  <label style={{ fontWeight: '600', marginBottom: '8px', display: 'block' }}>Từ ngày</label>
                  <input
                    type="date"
                    name="startDate"
                    value={filters.startDate}
                    onChange={handleFilterChange}
                    style={{ 
                      padding: '12px',
                      borderRadius: '8px',
                      border: '2px solid #e0e0e0',
                      fontSize: '14px'
                    }}
                  />
                </div>
                <div className="filter-item">
                  <label style={{ fontWeight: '600', marginBottom: '8px', display: 'block' }}>Đến ngày</label>
                  <input
                    type="date"
                    name="endDate"
                    value={filters.endDate}
                    onChange={handleFilterChange}
                    style={{ 
                      padding: '12px',
                      borderRadius: '8px',
                      border: '2px solid #e0e0e0',
                      fontSize: '14px'
                    }}
                  />
                </div>
                <div className="filter-item">
                  <label style={{ fontWeight: '600', marginBottom: '8px', display: 'block' }}>Hệ điều hành</label>
                  <select
                    name="os"
                    value={filters.os}
                    onChange={handleFilterChange}
                    style={{ 
                      padding: '12px',
                      borderRadius: '8px',
                      border: '2px solid #e0e0e0',
                      fontSize: '14px'
                    }}
                  >
                    <option value="all">Tất cả</option>
                    <option value="iOS">iOS</option>
                    <option value="Android">Android</option>
                    <option value="Other">Khác</option>
                  </select>
                </div>
              </div>
              <div style={{ display: 'flex', gap: '12px' }}>
                <button className="btn btn-primary" onClick={handleApplyFilter}>
                  ✓ Áp dụng bộ lọc
                </button>
                <button className="btn btn-success" onClick={handleExport}>
                  📊 Xuất báo cáo CSV
                </button>
              </div>
            </div>

            <div className="card">
            <h3 style={{ marginBottom: '20px', fontSize: '20px', fontWeight: '700' }}>
              📈 Biểu đồ truy cập (30 ngày gần nhất)
            </h3>
            <ResponsiveContainer width="100%" height={350}>
              <LineChart data={getChartData()}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />
                <XAxis dataKey="date" style={{ fontSize: '12px' }} />
                <YAxis style={{ fontSize: '12px' }} />
                <Tooltip 
                  contentStyle={{ 
                    borderRadius: '8px', 
                    border: 'none',
                    boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
                  }}
                />
                <Legend wrapperStyle={{ fontSize: '14px', fontWeight: '600' }} />
                <Line 
                  type="monotone" 
                  dataKey="iOS" 
                  stroke="#667eea" 
                  strokeWidth={3}
                  dot={{ fill: '#667eea', r: 4 }}
                />
                <Line 
                  type="monotone" 
                  dataKey="Android" 
                  stroke="#11998e" 
                  strokeWidth={3}
                  dot={{ fill: '#11998e', r: 4 }}
                />
                <Line 
                  type="monotone" 
                  dataKey="Other" 
                  stroke="#fa709a" 
                  strokeWidth={3}
                  dot={{ fill: '#fa709a', r: 4 }}
                />
              </LineChart>
            </ResponsiveContainer>
            </div>

            <div className="card">
              <h3 style={{ marginBottom: '20px', fontSize: '20px', fontWeight: '700' }}>
              📋 Chi tiết lượt truy cập
            </h3>
              <div style={{ overflowX: 'auto' }}>
                <table>
                  <thead>
                    <tr>
                      <th>Thời gian</th>
                      <th>Hệ điều hành</th>
                      <th>Phiên bản</th>
                      <th>Trình duyệt</th>
                      <th>Thiết bị</th>
                      <th>IP</th>
                    </tr>
                  </thead>
                  <tbody>
                    {stats?.clicks?.length > 0 ? (
                      stats.clicks.map((click, index) => (
                        <tr key={index}>
                          <td style={{ fontWeight: '500', fontSize: '13px' }}>
                            {new Date(click.timestamp).toLocaleString('vi-VN')}
                          </td>
                          <td>
                            <span style={{
                              padding: '6px 12px',
                              borderRadius: '20px',
                              fontSize: '12px',
                              fontWeight: '600',
                              background: click.os === 'iOS' ? 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' : 
                                        click.os === 'Android' ? 'linear-gradient(135deg, #11998e 0%, #38ef7d 100%)' : 
                                        'linear-gradient(135deg, #fa709a 0%, #fee140 100%)',
                              color: 'white'
                            }}>
                              {click.os}
                            </span>
                          </td>
                          <td style={{ color: '#666', fontSize: '13px' }}>
                            {click.osVersion || '-'}
                          </td>
                          <td style={{ color: '#666', fontSize: '13px' }}>
                            {click.browser || '-'}
                          </td>
                          <td style={{ color: '#666', fontSize: '13px' }}>
                            {click.device || '-'}
                          </td>
                          <td style={{ 
                            fontFamily: 'monospace', 
                            fontSize: '12px',
                            color: '#999'
                          }}>
                            {click.ip || '-'}
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan="6" style={{ textAlign: 'center', padding: '40px', color: '#999' }}>
                          Chưa có dữ liệu truy cập
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

export default Dashboard;
